import React, {useEffect, useState} from "react";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import OrderDetails from "../order/OrderDetails";
import { Helmet, HelmetProvider } from 'react-helmet-async';
import Header from "../Header/Header";
import Footer from "../../../../components/Footer";
import Loader from "../../../../components/Loader/Loader";
import Breadcrumbs from "../../../../components/Checkout/Breadcrumbs/BreadCrumbs";
import PassengersDetails from "../../../../components/Checkout/Passengers/PassengersDetails";
import {getPromoterService} from "../../../../services/getPromoterService";
import {getOrder, postPassengers} from "../../../../services/orderService";
import useDataTravel from '../../../../hooks/useDataTravel';
import MetaTags from "../../../../components/MetaTags";

const Passengers = () => {
    const currentStep = 0; // this is an index representing the breadcrumb step

    const [promoter, setPromoter] = useState(null);
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isFormValid, setIsFormValid] = useState(false);
    const [passengers, setPassengers] = useState([]);
    const [numPassengers, setNumPassengers] = useState(1);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    const [isReservation, setIsReservation] = useState(false);
    const [haveTime, setHaveTime] = useState(false);
    const [localTravelData, setLocalTravelData] = useState(null);

    const { fetchData } = useDataTravel();

    const numPassengersInParams = isNaN(searchParams.get("num_passengers")) ? null : Number(searchParams.get("num_passengers"))
    const MAX_NUM_PASSENGERS = 10;

    useEffect(() => {
       
        const fetchPromoter = async () => {
            try {
                const data = await getPromoterService();
                setPromoter(data);
                const order = await getOrder();
                setOrder(order);
                setHaveTime(order.booking.quotation.has_available_time_spots)
                setIsReservation(order.booking.quotation.is_reservation);


               const _travelData = await fetchData(order.booking.quotation.public_id);
               setLocalTravelData(_travelData);

                if (order.passengers[0]) {
                    setPassengers(
                        order.passengers.map((passenger) => {
                            return {
                                id: passenger.public_id,
                                fullName: passenger.full_name,
                                phone: passenger.phone,
                                email: passenger.email,
                                dob: passenger.date_of_birth,
                                idType: passenger.id_type,
                                idNumber: passenger.id_number,
                                nationality: passenger.nationality,
                                errors: {},
                            };
                        })
                    );
                    setNumPassengers(order.passengers.length);
                } else if (numPassengersInParams) {
                    setNumPassengers(Math.min(order.booking.quotation.stock, numPassengersInParams, MAX_NUM_PASSENGERS))
                } else {
                    setPassengers([
                        {
                            fullName: "",
                            phone: "",
                            email: "",
                            nationality: "",
                            dob: "",
                            idType: "",
                            idNumber: "",
                            errors: {},
                        },
                    ]);
                }

                setLoading(false);
            } catch (e) {
                console.log(e);
                //window.alert("Order not found!");
                //localStorage.removeItem("tp_order_id")
                navigate("/");
            }
        };
        fetchPromoter();
       
    }, [navigate, numPassengersInParams, fetchData]);

    useEffect(() => {
        const newPassengers = [...passengers];
        if (numPassengers > passengers.length) {
            for (let i = passengers.length; i < numPassengers; i++) {
                newPassengers.push({
                    fullName: "",
                    phone: "",
                    email: "",
                    nationality: "",
                    dob: "",
                    idType: "",
                    idNumber: "",
                    errors: {},
                });
            }
        } else if (numPassengers < passengers.length) {
            newPassengers.splice(numPassengers);
        }
        setPassengers(newPassengers);
        setNumPassengers(newPassengers.length)
        // eslint-disable-next-line
    }, [numPassengers]);

    useEffect(() => {
        const formValid = passengers.every(
            (passenger) =>
                passenger.fullName &&
                passenger.phone &&
                passenger.email &&
                passenger.nationality &&
                passenger.dob &&
                passenger.idType &&
                passenger.idNumber &&
                !passenger.errors.fullName &&
                !passenger.errors.phone &&
                !passenger.errors.email &&
                !passenger.errors.nationality &&
                !passenger.errors.dob &&
                !passenger.errors.idType &&
                !passenger.errors.idNumber
        );
        setIsFormValid(formValid);
    }, [passengers]);

    useEffect(() => {
        setIsSmallScreen(window.innerWidth <= 768);

        const handleResize = () => {
            setIsSmallScreen(window.innerWidth <= 768);
        };
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [isSmallScreen]);

    const handlePassengerChange = (index, field, value) => {
        const newPassengers = [...passengers];
        newPassengers[index] = {...newPassengers[index], [field]: value};
        setPassengers(newPassengers);
    };

    const handlePassengerBlur = (index, field, validateFn) => {
        const newPassengers = [...passengers];
        const error = validateFn(newPassengers[index][field]);
        newPassengers[index] = {
            ...newPassengers[index],
            errors: {...newPassengers[index].errors, [field]: error},
        };
        setPassengers(newPassengers);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isFormValid) {
            setLoading(true);
            try {
                const passengersPayload = {
                    passengers: passengers.map((passenger) => {
                        const passengerItem = {
                            full_name: passenger.fullName,
                            phone: passenger.phone,
                            email: passenger.email,
                            date_of_birth: passenger.dob,
                            id_type: passenger.idType,
                            id_number: passenger.idNumber,
                            nationality: passenger.nationality
                        }

                        if (passenger.id) {
                            passengerItem.public_id = passenger.id;
                        }
                        ;

                        return passengerItem;
                    })
                }

                const postResponse = await postPassengers(passengersPayload, order.booking.public_id);
                navigate("/checkout/billing");
            } catch (e) {
                console.log(e);
                window.alert("There was a problem with the server!");
                setLoading(false);
            }
        }
    };

    if (loading) return <Loader/>;

    const currentUrl = window.location.protocol + "//" + window.location.host +  window.location.pathname + window.location.search


    return (

        <HelmetProvider>
            <MetaTags
                name={promoter.fullname}
                description={order?.booking?.quotation?.itinerary?.description}
                icon={promoter.logoURL}
                title={order?.booking?.quotation?.title}
                url={currentUrl}
                font={localTravelData.data.font ? localTravelData.data.font : null}
                primaryColor={localTravelData.data.colors.primary_color ? localTravelData.data.colors.primary_color : null}
                secondaryColor={localTravelData.data.colors.secondary_color ? localTravelData.data.colors.secondary_color : null}
            />
            <Header promoter={promoter}></Header>
            <div className="passengers__container">
                <Breadcrumbs currentStep={currentStep}></Breadcrumbs>
                <div className="main-content">
                    <PassengersDetails
                        passengers={passengers}
                        onChange={handlePassengerChange}
                        onBlur={handlePassengerBlur}
                    />
                    <OrderDetails
                        numPassengers={numPassengers}
                        setNumPassengers={setNumPassengers}
                        quotation={order.booking.quotation}
                        passengers={passengers}
                        MAX_NUM_PASSENGERS={MAX_NUM_PASSENGERS}
                        handleSubmitNextStep={handleSubmit}
                        isFormValid={isFormValid}
                        nextStepLabel={"Siguiente paso"}
                        isReservation={isReservation}
                        selectDate={order.booking.selected_date ? order.booking.selected_date : ''}
                        haveTime={haveTime}
                        selectedOption={order.booking.selected_option ? order.booking.selected_option : ''}
                    />
                </div>
                {!isSmallScreen && (
                    <div className="checkout-actions">
                        <button type="button" onClick={handleSubmit} disabled={!isFormValid}>
                            Siguiente paso
                        </button>
                    </div>
                )}

            </div>
            <Footer promoter={promoter}></Footer>
        </HelmetProvider>
    );
};

export default Passengers;
